import React, { ReactNode, ReactElement } from 'react';
import { Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import MobilePagination from './MobilePagination';
import CustomPagination from '../CustomPagination';
import './tableStyle.css';
import { NoDataCross } from '../icons';
import { PagingParams } from '../../interfaces/Filter';

export interface tableTileProps {
    title: string;
    isNotTable?: boolean;
    noDataText?: () => ReactNode;
    columns?: ColumnsType<any>;
    rowKey?: string;
    actionText?: () => ReactNode;
    data?: { [key: string]: string }[];
    getData?: () => void;
}

export interface CustomTableProps {
    data: any;
    loadingData?: boolean;
    updateCurrentData?: (value: any) => void;
    mobileComponent?: ReactElement<any>;
    emptyActionText?: string;
    emptyAction?: () => void;
    onMobileRowClick?: (data: { [key: string]: string } | any) => void;
    emptyText?: string | ReactNode;
    columns?: ColumnsType<any>;
    scroll?: { [key: string]: number };
    customMobilePagination?: ReactElement<any>;
    showPagination?: boolean;
    paging?: PagingParams;
    updatePage?: (page: number) => void;
}

const CustomTable = ({
    data,
    loadingData,
    updateCurrentData,
    mobileComponent,
    emptyActionText,
    emptyAction,
    onMobileRowClick = () => {},
    emptyText,
    columns,
    scroll,
    customMobilePagination,
    showPagination,
    paging,
    updatePage
}: CustomTableProps) => {
    const dataToView = loadingData ? [] : data || [];

    const itemRender = (
        page: number,
        type: string,
        originalElement: React.ReactNode
    ) => {
        if (type === 'page') {
            return (
                <button className='btn-pagination page'>
                    {originalElement}
                </button>
            );
        }
        if (type === 'prev') {
            return null;
        }
        if (type === 'next') {
            return null;
        }
        return originalElement;
    };

    return (
        <>
            <div className='relative mb-4 hidden lg:block'>
                <Table
                    onRow={(record, rowIndex) => {
                        return {
                            style: { cursor: 'pointer' },
                            onClick: (event) => {
                                const target = event.target as HTMLElement;
                                if (target.closest('.no-click')) {
                                    return;
                                }

                                if (updateCurrentData !== undefined) {
                                    updateCurrentData(record);
                                }
                            }
                        };
                    }}
                    pagination={
                        showPagination && !paging && !updatePage
                            ? {
                                  pageSize: 20,
                                  itemRender,
                                  showSizeChanger: false,
                                  align: 'center',
                                  className: 'custom-pagination-container'
                              }
                            : false
                    }
                    loading={loadingData}
                    columns={columns}
                    dataSource={dataToView}
                    scroll={scroll}
                    rowKey='id'
                    locale={{
                        emptyText: () => {
                            return (
                                <div className='flex flex-col justify-center mt-6 mb-4 align-center bg-white min-h-[40vh]'>
                                    <div className='flex justify-center mb-4'>
                                        <NoDataCross />
                                    </div>

                                    <p className='text-center text-appcolorblackthree'>
                                        {emptyText}
                                    </p>

                                    {emptyAction && (
                                        <button
                                            className='cursor-pointer text-appcolorprimary'
                                            onClick={emptyAction}
                                        >
                                            {emptyActionText}
                                        </button>
                                    )}
                                </div>
                            );
                        }
                    }}
                />
                {showPagination && paging && updatePage && data.length > 0 && (
                    <CustomPagination {...{ paging, updatePage }} />
                )}
            </div>

            {mobileComponent && (
                <div className='lg:hidden min-h-[50vh] px-4  bg-white'>
                    {loadingData ? (
                        <div className='flex flex-col h-[60vh] justify-center mt-24 mb-24 items-center'>
                            <Spin />
                        </div>
                    ) : !dataToView?.length ? (
                        <div className='flex flex-col justify-center pt-8 mb-24 align-center'>
                            <div className='flex justify-center mb-4'>
                                <img
                                    src='/img/vectors/nodatacross.svg'
                                    alt='nodata'
                                />
                            </div>

                            <p className='text-center text-appcolorblackthree'>
                                {emptyText ||
                                    'There are no items on this list.'}
                            </p>

                            {emptyAction && (
                                <button
                                    className='cursor-pointer text-appcolorprimary'
                                    onClick={emptyAction}
                                >
                                    {emptyActionText}
                                </button>
                            )}
                        </div>
                    ) : (
                        dataToView?.map((item: { [key: string]: string }) =>
                            React.cloneElement(mobileComponent, {
                                key: item.title,
                                item,
                                onClick: (event: React.MouseEvent) => {
                                    const target = event.target as HTMLElement;
                                    if (target.closest('.no-click')) {
                                        return;
                                    }

                                    onMobileRowClick?.(item);
                                }
                            })
                        )
                    )}

                    {customMobilePagination ? (
                        customMobilePagination
                    ) : dataToView.length ? (
                        <MobilePagination
                            page={paging?.page || 1}
                            setPage={updatePage}
                            totalPages={Math.ceil(
                                (paging?.total || 1) / (paging?.size || 1)
                            )}
                        />
                    ) : null}
                </div>
            )}
        </>
    );
};

export default CustomTable;
